import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { listStaff, createStaff, updateStaff, listSubjects } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";
import ResizableTable from "../../components/ResizableTable";

const KNOWN_DEPARTMENTS = ["Giáo viên THPT", "Phòng Đào tạo", "Phòng Hành chính", "Bếp - Tạp vụ", "Ban an ninh", "Canteen"];

// Đọc cột Excel không phụ thuộc hoa/thường hay khoảng trắng thừa trong tiêu
// đề (dữ liệu nguồn thực tế hay lệch kiểu "Họ Tên" vs "Họ tên", thừa dấu
// cách...) — tránh việc lệch 1 ký tự làm rớt toàn bộ dòng khi nhập.
function normKey(k) {
  return String(k || "").trim().replace(/\s+/g, " ").toLowerCase();
}
function normalizeRow(row) {
  const out = {};
  Object.entries(row).forEach(([k, v]) => {
    out[normKey(k)] = v;
  });
  return out;
}
// Đổi vai trò -> icon (rê chuột xem chữ đầy đủ). Giáo viên phổ thông cũng hiện.
function roleIcon(title) {
  const t = (title || "").trim().toLowerCase();
  if (!t) return "";
  if (t.includes("phó hiệu trưởng")) return "🎖️";
  if (t.includes("hiệu trưởng")) return "👑";
  if (t.includes("trưởng khối") || t.includes("trưởng phòng") || t.includes("tổ trưởng")) return "📋";
  if (t.includes("bảo vệ") || t.includes("an ninh")) return "🛡️";
  if (t.includes("bếp") || t.includes("tạp vụ") || t.includes("cấp dưỡng")) return "🍳";
  if (t.includes("canteen") || t.includes("căng tin")) return "🍽️";
  if (t.includes("y tế") || t.includes("y sĩ")) return "⚕️";
  if (t.includes("kế toán")) return "🧮";
  if (t.includes("lái xe") || t.includes("tài xế")) return "🚌";
  if (t === "giáo viên" || t.startsWith("gv ") || t.startsWith("giáo viên")) return "👨‍🏫";
  return "🏷️"; // vai trò khác
}

// Chú thích icon vai trò (nguồn dùng chung cho bảng chú thích).
const ROLE_LEGEND = [
  ["👑", "Hiệu trưởng"],
  ["🎖️", "Phó hiệu trưởng"],
  ["📋", "Trưởng khối / phòng, Tổ trưởng"],
  ["👨‍🏫", "Giáo viên (phổ thông & chuyên môn)"],
  ["🛡️", "Bảo vệ / An ninh"],
  ["🍳", "Bếp / Tạp vụ"],
  ["🍽️", "Canteen"],
  ["⚕️", "Y tế"],
  ["🧮", "Kế toán"],
  ["🚌", "Lái xe"],
  ["🏷️", "Vai trò khác"],
];

function RoleLegend() {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{ background: "none", border: "none", color: "#0f766e", cursor: "pointer", fontSize: 13, padding: 0, flexShrink: 0, whiteSpace: "nowrap" }}
      >
        {open ? "▾" : "▸"} Chú thích icon vai trò
      </button>
      {open && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", marginTop: 8, padding: "10px 12px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8 }}>
          {ROLE_LEGEND.map(([icon, label]) => (
            <span key={label} style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 16 }}>{icon}</span>
              {label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
function field(nrow, ...candidates) {
  for (const c of candidates) {
    const v = nrow[normKey(c)];
    if (v !== undefined && v !== null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

const PROFILE_TEXT_FIELDS = [
  ["role", "Vai trò (phẩy = nhiều)"],
  ["employmentType", "Hình thức HĐ / Loại lao động"],
  // Chỉ dùng cho Thỉnh giảng: 2 = dạy lớp 12 môn thi TN (>= 4 tiết/tuần), 1 = còn lại -> đơn giá tiết TC 130k / 80k.
  ["tgLevel", "Level (cấp bậc) Thỉnh giảng: 1 hoặc 2"],
  ["teachingLoadPerWeek", "Số tiết giảng dạy/tuần"],
  ["dob", "Ngày sinh"],
  ["gender", "Giới tính"],
  ["phone", "SĐT"],
  ["cccd", "Số CCCD"],
  ["cccdIssueDate", "Ngày cấp CCCD"],
  ["contractStart", "Ngày bắt đầu HĐ"],
  ["contractEnd", "Ngày kết thúc HĐ"],
  ["workStartDate", "Ngày bắt đầu làm việc"],
  ["thamNienCongTac", "Thâm niên công tác (năm)"],
  ["khongApDungThamNien", "Không áp dụng thâm niên"],
  ["resignDate", "Ngày nghỉ việc"],
  ["status", "Trạng thái"],
  ["currentUnit", "Đơn vị đang công tác"],
  ["specialty", "Chuyên môn"],
  ["taxCode", "MST"],
  ["bankName", "Ngân hàng nhận lương"],
  ["bankAccount", "Số tài khoản"],
  ["homeAddress", "Địa chỉ nơi ở"],
  ["currentAddress", "Nơi ở hiện tại"],
  ["workAddress", "Địa chỉ công tác"],
  ["relative1", "Người thân 1"],
  ["relative1Phone", "SĐT người thân 1"],
  ["relative2", "Người thân 2"],
  ["relative2Phone", "SĐT người thân 2"],
  ["note", "Ghi chú"],
];

// Form chi tiết nhân sự dạng modal: mở lên CHỈ ĐỌC, bấm "Sửa" mới cho chỉnh.
function StaffDetailModal({ staff, subjects, onSave, onClose }) {
  // roles = [{title, dept}]; ô "Vai trò" chỉ sửa vai trò Ở KHỐI ĐANG XEM.
  const viewDept = staff.department;
  const roleObjs = (Array.isArray(staff.roles) ? staff.roles : staff.role ? [staff.role] : []).map((r) =>
    typeof r === "object" ? r : { title: r, dept: viewDept }
  );
  const buildFields = () => {
    const base = Object.fromEntries(PROFILE_TEXT_FIELDS.map(([key]) => [key, staff[key] || ""]));
    base.role = roleObjs.filter((r) => r.dept === viewDept).map((r) => r.title).join(", ");
    return base;
  };
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(staff.name);
  const [subjectIds, setSubjectIds] = useState(staff.subjectIds || []);
  const [fields, setFields] = useState(buildFields);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const cancelEdit = () => {
    setName(staff.name);
    setSubjectIds(staff.subjectIds || []);
    setFields(buildFields());
    setError("");
    setEditing(false);
  };

  const save = async () => {
    if (!name.trim()) {
      setError("Thiếu họ tên.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const { role, ...rest } = fields;
      const otherRoles = roleObjs.filter((r) => r.dept !== viewDept);
      const typed = String(role || "").split(",").map((t) => t.trim()).filter(Boolean);
      const roles = [...otherRoles, ...typed.map((t) => ({ title: t, dept: viewDept }))];
      await onSave({ name: name.trim(), subjectIds, ...rest, roles });
      setEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const subjNames = subjects.filter((s) => subjectIds.includes(s.id)).map((s) => s.name).join(", ") || "—";
  const displayRows = [
    ["Vai trò (ở Khối này)", fields.role],
    ["Môn dạy", subjNames],
    ...PROFILE_TEXT_FIELDS.filter(([k]) => k !== "role").map(([k, label]) => [label.replace(" (phẩy = nhiều)", ""), k === "khongApDungThamNien" ? (String(fields[k] || "").trim() ? "✔ Có (tiết vượt luôn 80.000)" : "") : fields[k]]),
  ];

  return (
    <div style={overlayStyle} onClick={editing ? undefined : onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {editing ? (
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Họ tên" style={{ ...modalInputStyle, fontSize: 18, fontWeight: 700, marginBottom: 4 }} />
        ) : (
          <h3 style={{ margin: "0 0 4px 0" }}>{name}</h3>
        )}
        <p style={{ margin: "0 0 14px 0", color: "#6b7280", fontSize: 13 }}>Khối: {staff.department}</p>

        {editing ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label style={fieldLabelStyle}>
              <span style={fieldSpanStyle}>Môn dạy</span>
              <select multiple value={subjectIds} onChange={(e) => setSubjectIds(Array.from(e.target.selectedOptions, (o) => o.value))} style={{ ...modalInputStyle, height: 90 }}>
                {subjects.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
              </select>
            </label>
            <label style={fieldLabelStyle}>
              <span style={fieldSpanStyle}>Vai trò ở Khối này (phẩy = nhiều)</span>
              <input value={fields.role} onChange={(e) => setFields((f) => ({ ...f, role: e.target.value }))} style={modalInputStyle} />
            </label>
            {PROFILE_TEXT_FIELDS.filter(([k]) => k !== "role").map(([key, label]) => (
              <label key={key} style={fieldLabelStyle}>
                <span style={fieldSpanStyle}>{label}</span>
                {key === "khongApDungThamNien" ? (
                  // Ô tích: có tích = luôn tính tiết vượt 80.000, không theo thâm niên.
                  <input type="checkbox" checked={!!String(fields[key] || "").trim()} onChange={(e) => setFields((f) => ({ ...f, [key]: e.target.checked ? "x" : "" }))} style={{ width: 18, height: 18 }} />
                ) : (
                  <input value={fields[key]} onChange={(e) => setFields((f) => ({ ...f, [key]: e.target.value }))} style={modalInputStyle} />
                )}
              </label>
            ))}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {displayRows.map(([label, value]) => (
              <div key={label} style={fieldLabelStyle}>
                <span style={fieldSpanStyle}>{label}</span>
                <div style={readValueStyle}>{value || "—"}</div>
              </div>
            ))}
          </div>
        )}

        {error && <p style={{ color: "#dc2626", marginTop: 10 }}>{error}</p>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          {editing ? (
            <>
              <button type="button" onClick={cancelEdit} style={secondaryBtnStyle}>Huỷ</button>
              <button type="button" onClick={save} disabled={saving} style={btnStyle}>{saving ? "Đang lưu..." : "Lưu"}</button>
            </>
          ) : (
            <>
              <button type="button" onClick={onClose} style={secondaryBtnStyle}>Đóng</button>
              <button type="button" onClick={() => setEditing(true)} style={btnStyle}>Sửa</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const StaffListPage = ({ title, departmentFilter, lockDepartment }) => {
  const schoolId = getActiveSchoolId();
  const [staffList, setStaffList] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [name, setName] = useState("");
  const [department, setDepartment] = useState(lockDepartment ? departmentFilter : "");
  const [subjectIds, setSubjectIds] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [importSummary, setImportSummary] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [showAdvSearch, setShowAdvSearch] = useState(false);
  const [dobMonth, setDobMonth] = useState(""); // "" = tất cả, "1".."12" = lọc theo tháng sinh
  const [advFilters, setAdvFilters] = useState({}); // { [fieldKey]: text } — lọc theo từng trường hồ sơ

  // Nút chuyển Khối chỉ có ở trang không khoá Khối (Nhân sự toàn trường).
  // null = đang xem "Toàn trường".
  const [selectedDept, setSelectedDept] = useState(null);
  const [deptSummary, setDeptSummary] = useState([]); // [{name, count}]
  const [totalCount, setTotalCount] = useState(0);

  const effectiveFilter = lockDepartment ? departmentFilter : selectedDept;

  // Tìm kiếm gọi thẳng API (?q=...) — server quét toàn trường, không phải
  // lọc trên mảng đã tải sẵn ở client.
  const reload = () => {
    if (!schoolId) return;
    setLoading(true);
    Promise.all([listStaff(schoolId, { department: effectiveFilter, q: nameFilter }), listSubjects(schoolId)])
      .then(([s, subs]) => {
        setStaffList(s);
        setSubjects(subs);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  // Danh sách nút Khối + số lượng — đọc riêng "toàn trường" 1 lần, không phụ
  // thuộc Khối đang chọn, để thanh nút luôn đủ và đúng số lượng.
  const reloadDeptSummary = () => {
    if (!schoolId || lockDepartment) return;
    listStaff(schoolId, {})
      .then((all) => {
        const counts = {};
        all.forEach((s) => {
          const d = s.department || "(chưa rõ)";
          counts[d] = (counts[d] || 0) + 1;
        });
        setDeptSummary(Object.entries(counts).map(([name, count]) => ({ name, count })).sort((a, b) => a.name.localeCompare(b.name)));
        setTotalCount(all.length);
      })
      .catch(() => {});
  };

  useEffect(reload, [schoolId, effectiveFilter, nameFilter]);
  useEffect(reloadDeptSummary, [schoolId, lockDepartment]);
  useEffect(() => {
    if (!lockDepartment) setDepartment(selectedDept || "");
    // eslint-disable-next-line
  }, [selectedDept]);

  if (!schoolId) {
    return (
      <div style={{ padding: 24 }}>
        Chưa chọn trường. Vào <Link to="/happyschool/schools">Trường của tôi</Link> trước.
      </div>
    );
  }

  const handleCreate = async (e) => {
    e.preventDefault();
    const dept = lockDepartment ? departmentFilter : department.trim();
    if (!name.trim() || !dept) return;
    try {
      await createStaff(schoolId, { name: name.trim(), department: dept, subjectIds });
      setName("");
      setSubjectIds([]);
      if (!lockDepartment) setDepartment(selectedDept || "");
      reload();
      reloadDeptSummary();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdate = async (staffId, data) => {
    await updateStaff(schoolId, staffId, data);
    // Không đóng modal — cho xem lại ở chế độ chỉ đọc sau khi lưu.
    reload();
    reloadDeptSummary();
  };

  const subjectNames = (ids) =>
    (ids || []).map((id) => subjects.find((s) => s.id === id)?.name || id).join(", ") || "—";

  // Lấy tháng sinh từ dob, hỗ trợ "yyyy-mm-dd" (chuẩn) và "d/m/yyyy" (nhập tay).
  // Trả về null nếu dob trống hoặc không đủ thông tin tháng (VD chỉ có năm).
  const monthOfDob = (dob) => {
    const s = String(dob || "").trim();
    let m = s.match(/^\d{4}-(\d{1,2})-\d{1,2}$/);
    if (m) return Number(m[1]);
    m = s.match(/^\d{1,2}\/(\d{1,2})\/\d{4}$/);
    if (m) return Number(m[1]);
    return null;
  };

  // Lấy giá trị text của 1 trường để lọc — "role" là mảng {title,dept} nên gộp
  // các tiêu đề lại; các trường còn lại lấy thẳng chuỗi.
  const advFieldText = (s, key) => {
    if (key === "role") {
      const arr = Array.isArray(s.roles) ? s.roles : s.role ? [s.role] : [];
      return arr.map((r) => (typeof r === "object" ? r.title : r)).join(", ");
    }
    return s[key] != null ? String(s[key]) : "";
  };
  const activeAdvFilters = Object.entries(advFilters).filter(([, v]) => v.trim());

  // nameFilter đã lọc ở server (reload gọi API kèm ?q=), staffList về đây là
  // đúng kết quả tìm kiếm rồi — chỉ còn lọc thêm tháng sinh/trường nâng cao.
  const filteredStaffList = staffList.filter((s) => {
    if (dobMonth && monthOfDob(s.dob) !== Number(dobMonth)) return false;
    for (const [key, val] of activeAdvFilters) {
      if (!advFieldText(s, key).toLowerCase().includes(val.trim().toLowerCase())) return false;
    }
    return true;
  });

  const runSearch = (e) => {
    e?.preventDefault();
    const term = searchInput.trim();
    setNameFilter(term);
    // Tìm kiếm phải quét cả trường, không chỉ trong Khối đang xem — tự
    // chuyển về "Toàn trường" khi có từ khoá tìm, tránh hiểu nhầm là không
    // tìm thấy trong khi thực ra chỉ đang lọc trong 1 Khối.
    if (term && !lockDepartment) setSelectedDept(null);
  };

  // ---- Xuất Excel ----
  const handleExport = () => {
    const rows = filteredStaffList.map((s) => ({
      "Họ tên": s.name,
      "Khối": s.department || "",
      "Vai trò": (s.roles || []).map((r) => (typeof r === "object" ? `${r.title} (${r.dept})` : r)).join("; "),
      "Môn dạy": subjectNames(s.subjectIds).replace(/—/, ""),
      "Loại lao động": s.employmentType || "",
      "Số tiết giảng dạy/Tuần": s.teachingLoadPerWeek || "",
      "Ngày sinh": s.dob || "",
      "Giới tính": s.gender || "",
      "SĐT": s.phone || "",
      "CCCD": s.cccd || "",
      "Ngày bắt đầu hợp đồng": s.contractStart || "",
      "Ngày kết thúc hợp đồng": s.contractEnd || "",
      "Trạng thái": s.status || "",
      "Địa chỉ nơi ở": s.homeAddress || "",
      "Địa chỉ công tác": s.workAddress || "",
      "Ghi chú": s.note || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Nhân sự");
    XLSX.writeFile(wb, lockDepartment ? "danh_sach_giao_vien.xlsx" : "danh_sach_nhan_su.xlsx");
  };

  // ---- Tải file Excel mẫu (chỉ tiêu đề cột, để điền rồi Nhập Excel lại) ----
  const handleDownloadTemplate = () => {
    const headers = [
      "Họ tên", "Khối", "Vai trò", "Môn dạy", "Loại lao động", "Số tiết giảng dạy/Tuần",
      "Ngày sinh", "Giới tính", "SĐT", "CCCD", "Ngày bắt đầu hợp đồng", "Ngày kết thúc hợp đồng",
      "Trạng thái", "Địa chỉ nơi ở", "Địa chỉ công tác", "Ghi chú",
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Mẫu nhân sự");
    XLSX.writeFile(wb, "mau_nhap_nhan_su.xlsx");
  };

  // ---- Nhập Excel: cột "Họ tên" bắt buộc; "Khối" chỉ cần điền ở dòng đầu
  // mỗi nhóm (forward-fill), giống cấu trúc file mẫu DS CBCNV. Nếu trang
  // đang khoá Khối (Giáo viên THPT) thì bỏ qua cột Khối, ép tất cả về
  // departmentFilter.
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportSummary(null);
    setError("");
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });

      let ok = 0;
      const unmatchedSubjects = new Set();
      const skipped = [];
      let lastDept = "";
      for (const row of rows) {
        const nrow = normalizeRow(row);
        const rowName = field(nrow, "Họ tên", "Họ Tên", "Ten", "Name");
        if (!rowName) {
          skipped.push(JSON.stringify(row));
          continue;
        }
        const rawDept = field(nrow, "Khối");
        if (rawDept) lastDept = rawDept;
        const dept = lockDepartment ? departmentFilter : lastDept;
        if (!dept) {
          skipped.push(rowName + " (thiếu Khối)");
          continue;
        }

        const subjNames = field(nrow, "Môn dạy", "Chuyên môn")
          .split(/[,;]/)
          .map((s) => s.trim())
          .filter(Boolean);
        const ids = [];
        subjNames.forEach((sn) => {
          const found = subjects.find((s) => s.name.toLowerCase() === sn.toLowerCase());
          if (found) ids.push(found.id);
          else if (sn) unmatchedSubjects.add(sn);
        });

        await createStaff(schoolId, {
          name: rowName,
          department: dept,
          subjectIds: ids,
          roles: field(nrow, "Vai trò").split(",").map((r) => r.trim()).filter(Boolean).map((t) => ({ title: t, dept })),
          employmentType: field(nrow, "Loại lao động", "Hình thức HĐ", "Diện"),
          teachingLoadPerWeek: field(nrow, "Số tiết giảng dạy/Tuần", "Số tiết giảng dạy/ Tuần"),
          dob: field(nrow, "Ngày sinh"),
          gender: field(nrow, "Giới tính"),
          phone: field(nrow, "SĐT", "Số điện thoại"),
          cccd: field(nrow, "CCCD", "Số CCCD"),
          contractStart: field(nrow, "Ngày bắt đầu hợp đồng"),
          contractEnd: field(nrow, "Ngày kết thúc hợp đồng"),
          status: field(nrow, "Trạng thái"),
          homeAddress: field(nrow, "Địa chỉ nơi ở"),
          workAddress: field(nrow, "Địa chỉ công tác"),
          note: field(nrow, "Ghi chú", "Vị trí/Ghi chú"),
        });
        ok += 1;
      }
      setImportSummary({ ok, skipped: skipped.length, unmatchedSubjects: Array.from(unmatchedSubjects) });
      reload();
      reloadDeptSummary();
    } catch (err) {
      setError("Lỗi đọc file: " + err.message);
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const importHint =
    'Nhập Excel: cột "Họ tên" bắt buộc' +
    (!lockDepartment ? '; "Khối" chỉ cần điền ở dòng đầu mỗi nhóm (tự áp dụng cho các dòng dưới)' : "") +
    '. "Môn dạy" khớp tên môn đã tạo, còn lại đều tuỳ chọn. Chưa biết cột nào thì bấm "Tải excel mẫu" trước.';

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
      <h2 style={{ margin: "0 0 16px 0" }}>{title}</h2>

      {!lockDepartment && (
        <div style={{ display: "flex", gap: 8, flexWrap: "nowrap", overflowX: "auto", marginBottom: 20, paddingBottom: 4 }}>
          <button
            onClick={() => setSelectedDept(null)}
            style={selectedDept === null ? deptBtnActiveStyle : deptBtnStyle}
          >
            Toàn trường ({totalCount})
          </button>
          {deptSummary.map((d) => (
            <button
              key={d.name}
              onClick={() => setSelectedDept(d.name)}
              style={selectedDept === d.name ? deptBtnActiveStyle : deptBtnStyle}
            >
              {d.name} ({d.count})
            </button>
          ))}
        </div>
      )}

      <form onSubmit={runSearch} style={{ marginBottom: 12, display: "flex", gap: 8, flexWrap: "nowrap", overflowX: "auto", alignItems: "center", paddingBottom: 4 }}>
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Họ và tên"
          style={{ ...inputStyle, minWidth: 220 }}
        />
        <button type="submit" style={secondaryBtnStyle}>
          🔍 Tìm kiếm
        </button>
        <button type="button" onClick={() => setShowAdvSearch((v) => !v)} style={(dobMonth || activeAdvFilters.length) ? btnStyle : secondaryBtnStyle}>
          🔎 Tìm kiếm nâng cao{(dobMonth || activeAdvFilters.length) ? ` • ${activeAdvFilters.length + (dobMonth ? 1 : 0)}` : ""}
        </button>
        <button type="button" onClick={() => setShowAddForm((v) => !v)} style={btnStyle}>
          + Thêm mới
        </button>
        <button type="button" onClick={handleExport} style={secondaryBtnStyle}>
          ⬇ Xuất Excel
        </button>
        <button type="button" onClick={() => fileInputRef.current?.click()} disabled={importing} style={secondaryBtnStyle} title={importHint}>
          {importing ? "Đang nhập..." : "⬆ Nhập excel"}
        </button>
        <button type="button" onClick={handleDownloadTemplate} style={secondaryBtnStyle}>
          📄 Tải excel mẫu
        </button>
        <span title={importHint} style={{ cursor: "help", color: "#0f766e", fontSize: 16 }}>ⓘ</span>
        <RoleLegend />
        <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleImportFile} style={{ display: "none" }} />
      </form>

      {showAdvSearch && (
        <div style={{ ...boxStyle, marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
              Tháng sinh:{" "}
              <select value={dobMonth} onChange={(e) => setDobMonth(e.target.value)} style={{ ...inputStyle, width: "auto" }}>
                <option value="">Tất cả</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>Tháng {m}</option>
                ))}
              </select>
            </label>
            {(dobMonth || activeAdvFilters.length > 0) && (
              <button type="button" onClick={() => { setDobMonth(""); setAdvFilters({}); }} style={secondaryBtnStyle}>
                ✕ Xoá hết bộ lọc nâng cao
              </button>
            )}
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>Lọc theo từng trường hồ sơ (gõ vào ô nào thì lọc theo ô đó, không phân biệt hoa/thường):</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 8 }}>
            {PROFILE_TEXT_FIELDS.map(([key, label]) => (
              <label key={key} style={{ fontSize: 12, color: "#4b5563", display: "flex", flexDirection: "column", gap: 3 }}>
                {label.replace(" (phẩy = nhiều)", "")}
                <input
                  value={advFilters[key] || ""}
                  onChange={(e) => setAdvFilters((f) => ({ ...f, [key]: e.target.value }))}
                  style={{ ...inputStyle, width: "100%" }}
                />
              </label>
            ))}
          </div>
        </div>
      )}

      {showAddForm && (
        <form
          onSubmit={handleCreate}
          style={{ ...boxStyle, marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}
        >
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Họ tên"
            style={inputStyle}
          />
          {!lockDepartment && (
            <>
              <input
                list="hs-department-suggestions"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                placeholder="Khối"
                style={inputStyle}
              />
              <datalist id="hs-department-suggestions">
                {KNOWN_DEPARTMENTS.map((d) => (
                  <option key={d} value={d} />
                ))}
              </datalist>
            </>
          )}
          <select
            multiple
            value={subjectIds}
            onChange={(e) => setSubjectIds(Array.from(e.target.selectedOptions, (o) => o.value))}
            style={{ ...inputStyle, minWidth: 200 }}
          >
            {subjects.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <button type="submit" style={btnStyle}>
            Lưu
          </button>
          <button type="button" onClick={() => setShowAddForm(false)} style={secondaryBtnStyle}>
            Huỷ
          </button>
        </form>
      )}

      {importSummary && (
        <div style={{ ...boxStyle, marginBottom: 16 }}>
          <p style={{ margin: 0 }}>Đã nhập {importSummary.ok} người. Bỏ qua {importSummary.skipped} dòng (thiếu tên/Khối).</p>
          {importSummary.unmatchedSubjects.length > 0 && (
            <p style={{ margin: "4px 0 0 0", color: "#b45309" }}>
              Môn không khớp (đã bỏ qua): {importSummary.unmatchedSubjects.join(", ")}
            </p>
          )}
        </div>
      )}

      {error && <p style={{ color: "#dc2626" }}>{error}</p>}

      <h3 style={{ margin: "0 0 4px 0", fontSize: 16 }}>Danh sách nhân sự ({filteredStaffList.length})</h3>
      {loading ? (
        <p>Đang tải...</p>
      ) : filteredStaffList.length === 0 ? (
        <p style={{ color: "#6b7280" }}>{nameFilter ? "Không tìm thấy ai khớp." : "Chưa có ai."}</p>
      ) : (
        <ResizableTable
          storageKey={lockDepartment ? "hs-staff-teachers" : "hs-staff-all"}
          maxHeight="70vh"
          rows={filteredStaffList}
          rowKey={(s) => s.id}
          columns={[
            {
              key: "name",
              header: "Họ tên",
              width: 200,
              sticky: true,
              align: "left",
              render: (s) => (
                <span onDoubleClick={() => setEditingId(s.id)} title="Bấm đúp để xem/sửa" style={{ cursor: "pointer" }}>
                  <strong>{s.name}</strong>
                  {roleIcon(s.displayRole) && (
                    <span title={s.displayRole} style={{ marginLeft: 6, fontSize: 15 }}>
                      {roleIcon(s.displayRole)}
                    </span>
                  )}
                </span>
              ),
            },
            { key: "status", header: "Trạng thái", width: 110, render: (s) => s.status || "" },
            lockDepartment
              ? { key: "mon", header: "Môn dạy", width: 170, render: (s) => subjectNames(s.subjectIds) }
              : { key: "khoi", header: "Khối", width: 150, render: (s) => s.department },
            { key: "phone", header: "SĐT", width: 120, render: (s) => s.phone || "" },
            { key: "employmentType", header: "Loại lao động", width: 140, render: (s) => s.employmentType || "" },
            { key: "tgLevel", header: "Level", width: 80, render: (s) => (String(s.employmentType || "").normalize("NFC") === "Thỉnh giảng" ? String(Number(s.tgLevel) === 2 ? 2 : 1) : "") },
            { key: "cccd", header: "CCCD", width: 130, render: (s) => s.cccd || "" },
            { key: "homeAddress", header: "Địa chỉ", width: 220, render: (s) => s.homeAddress || "" },
          ]}
        />
      )}

      {editingId && (() => {
        const s = filteredStaffList.find((x) => x.id === editingId) || staffList.find((x) => x.id === editingId);
        return s ? (
          <StaffDetailModal
            staff={s}
            subjects={subjects}
            onSave={(data) => handleUpdate(s.id, data)}
            onClose={() => setEditingId(null)}
          />
        ) : null;
      })()}

      {lockDepartment && (
        <p style={{ marginTop: 24, fontSize: 13 }}>
          Xem/khai báo <Link to="/happyschool/teachers/busy">lịch bận theo tuần của giáo viên</Link> ở trang riêng.
        </p>
      )}
    </div>
  );
};

const inputStyle = { padding: 8, border: "1px solid #d1d5db", borderRadius: 6, minWidth: 120, flexShrink: 0 };
const btnStyle = { padding: "8px 16px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, flexShrink: 0, whiteSpace: "nowrap" };
const secondaryBtnStyle = { padding: "6px 12px", background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", marginRight: 6, flexShrink: 0, whiteSpace: "nowrap" };
const boxStyle = { padding: 12, background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 8 };
const deptBtnStyle = { padding: "8px 14px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 20, cursor: "pointer", fontSize: 13, flexShrink: 0, whiteSpace: "nowrap" };
const deptBtnActiveStyle = { ...deptBtnStyle, background: "#0ea5a3", color: "#fff", borderColor: "#0ea5a3", fontWeight: 600 };
const overlayStyle = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 };
const modalStyle = { background: "#fff", borderRadius: 12, padding: 24, width: "min(760px, 100%)", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.2)" };
const fieldLabelStyle = { fontSize: 13, display: "flex", flexDirection: "column", gap: 4 };
const fieldSpanStyle = { color: "#4b5563", fontWeight: 600 };
const modalInputStyle = { padding: 7, border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, width: "100%", boxSizing: "border-box" };
const readValueStyle = { padding: "7px 8px", background: "#f9fafb", border: "1px solid #eef0f2", borderRadius: 6, minHeight: 18 };

export default StaffListPage;
